import moment from 'moment-timezone';
import { sortBy } from 'lodash';

/* @ngInject */
export default class {
  constructor($log) {
    this.$log = $log;

    /**
     * Keys from moment mapped to our custom names
     */
    const customZones = {
      'Pacific/Midway': 'Midway Island, Samoa',
      'Pacific/Honolulu': 'Hawaii',
      'America/Anchorage': 'Alaska',
      'America/Los_Angeles': 'Pacific Time',
      'America/Phoenix': 'Arizona',
      'America/Denver': 'Mountain Time',
      'America/Mexico_City': 'Mexico City',
      'America/Bogota': 'Bogota, Lima, Quito',
      'America/Chicago': 'Central Time',
      'America/New_York': 'Eastern Time',
      'America/Glace_Bay': 'Atlantic Time',
      'Atlantic/Azores': 'Azores',
      'Africa/Casablanca': 'Casablanca, Monrovia',
      'Europe/London': 'Dublin, Edinburgh, Lisbon, London',
      'Africa/Harare': 'Harare, Pretoria',
      'Europe/Sarajevo': 'Sarajevo, Skopje, Sofija, Vilnius, Warsaw, Zagreb',
      'Europe/Paris': 'Brussels, Copenhagen, Madrid, Paris',
      'Europe/Vienna': 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
      'Europe/Prague': 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
      'Africa/Cairo': 'Cairo',
      'Africa/Addis_Ababa': 'West Central Africa',
      'Asia/Kuwait': 'Kuwait, Riyadh',
      'Europe/Helsinki': 'Helsinki, Riga, Tallinn',
      'Europe/Moscow': 'Moscow, St. Petersburg, Volgograd',
      'Europe/Athens': 'Athens, Istanbul',
      'Africa/Nairobi': 'Nairobi',
      'Asia/Baghdad': 'Baghdad',
      'Asia/Jerusalem': 'Jerusalem',
      'Europe/Minsk': 'Minsk',
      'Europe/Bucharest': 'Bucharest',
      'Asia/Baku': 'Baku, Tbilisi, Yerevan',
      'Asia/Muscat': 'Abu Dhabi, Muscat',
      'Asia/Kabul': 'Kabul',
      'Asia/Tehran': 'Tehran',
      'Asia/Yekaterinburg': 'Yekaterinburg',
      'Asia/Tashkent': 'Islamabad, Karachi, Tashkent',
      'Asia/Kolkata': 'Sri Jayawardenepura',
      'Asia/Calcutta': 'Calcutta, Chennai, Mumbai, New Delhi',
      'Asia/Kathmandu': 'Kathmandu',
      'Asia/Dhaka': 'Astana, Dhaka',
      'Asia/Almaty': 'Almaty, Novosibirsk',
      'Asia/Bangkok': 'Bangkok',
      'Asia/Rangoon': 'Rangoon',
      'Asia/Krasnoyarsk': 'Krasnoyarsk',
      'Asia/Jakarta': 'Hanoi, Jakarta',
      'Asia/Hong_Kong': 'Hong Kong',
      'Asia/Shanghai': 'Beijing, Chongqing, Urumqi, Taipei',
      'Australia/Perth': 'Perth',
      'Asia/Irkutsk': 'Irkutsk, Ulaan Bataar',
      'Asia/Kuala_Lumpur': 'Kuala Lumpur, Singapore',
      'Asia/Seoul': 'Seoul',
      'Asia/Tokyo': 'Osaka, Sapporo, Tokyo',
      'Asia/Yakutsk': 'Yakutsk',
      'Australia/Adelaide': 'Adelaide',
      'Australia/Canberra': 'Canberra, Melbourne, Sydney',
      'Pacific/Guam': 'Guam, Port Moresby',
      'Australia/Hobart': 'Hobart',
      'Australia/Brisbane': 'Brisbane',
      'Asia/Vladivostok': 'Vladivostok',
      'Asia/Magadan': 'Magadan, Solomon, Is., New Caledonia',
      'Pacific/Fiji': 'Fiji, Kamchatka, Marshall Is.',
      'Pacific/Auckland': 'Auckland, Wellington',
      'Pacific/Tongatapu': 'Nukualofa'
    };

    /**
     * Gets all of the moment timezone names that are useful out into an array.
     */
    this.timezoneIdList = moment.tz.names().reduce((acc, timeZoneId) => {
      if (
        timeZoneId.match(
          /(?:(?:Africa)|(?:America)|(?:Antarctica)|(?:Asia)|(?:Atlantic)|(?:Australia)|(?:Europe)|(?:Indian)|(?:Pacific))\/[A-Za-z_]*/
        )
      ) {
        acc.push(timeZoneId);
      }
      return acc;
    }, []);

    /**
     * Custom zones from moment mapped to our custom names.
     * This validates that they're in Moment.
     */
    const customTimezoneMap = Object.keys(customZones).reduce((acc, zoneKey) => {
      if (this.timezoneIdList.includes(zoneKey)) {
        acc[zoneKey] = customZones[zoneKey];
      }

      return acc;
    }, {});

    /**
     * Creates an array of objects with the timeZoneId and display name for putting in a selector.
     */
    this.selectList = sortBy(
      Object.keys(customTimezoneMap).map(timezoneId => {
        const offset = moment()
          .tz(timezoneId)
          .format('Z');
        const abbr = this.abbr(undefined, timezoneId);

        return {
          value: timezoneId,
          name: `${offset !== '+00:00' ? offset : ''} ${abbr.hasAbbr ? `(${abbr.text})` : ''} ${customTimezoneMap[
            timezoneId
          ] || timezoneId}`
        };
      }),
      ({ value }) =>
        Number(
          moment()
            .tz(value)
            .format('Z')
            .replace(/[+:]/g, '')
        )
    );
  }

  /**
   * Accepts a timezoneId and returns an object with whether the abbreviations exists and the text you would use
   *
   * @param {*} timezoneId
   */
  abbr(date, timezoneId) {
    if (!this.timezoneIdList.includes(timezoneId)) {
      return {
        hasAbbr: false,
        text: timezoneId
      };
    }

    const abbr = moment(date)
      .tz(timezoneId)
      .format('z');
    const hasAbbr = !/\d/.test(abbr);

    return {
      hasAbbr,
      text: hasAbbr ? abbr : timezoneId.replace(/_/, ' ')
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/services/TimeZoneClass.js